/////////////////////////////////////////////////////////////////
// Grouping of hosted listing fields for the product page.       //
//                                                               //
// The listing fields themselves are configured by an admin in    //
// Sharetribe Console and delivered as a hosted asset, so we      //
// can't import their keys from anywhere in this repo. Instead we //
// recognise the handful of fields that the product page treats   //
// specially by their key *or* their label, tolerating the usual  //
// formatting differences (case, spaces, ampersands, hyphens).    //
/////////////////////////////////////////////////////////////////

// Group identifiers returned by classifyListingField.
export const FIELD_GROUP_ECOLABELS = 'ecolabels';
export const FIELD_GROUP_SUSTAINABILITY_DETAILS = 'sustainabilityDetails';
export const FIELD_GROUP_DELIVERY = 'delivery';
export const FIELD_GROUP_RETURNS = 'returns';
export const FIELD_GROUP_DISPATCH_LOCATION = 'dispatchLocation';

// Where shoppers can read how ethy verifies each ecolabel.
export const ETHY_STANDARDS_URL = 'https://ethy.co.uk/our-standards';

// "Processing & Delivery Estimate" and "processingDeliveryEstimate" both
// collapse to "processingdeliveryestimate", which is what makes matching on
// either the key or the label work.
const normalize = value =>
  String(value == null ? '' : value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

// Each group is recognised by a list of substrings. The order of GROUPS
// matters: "Provide supporting details for the sustainability criteria
// selected" also contains "sustainability criteria", so the details field has
// to be tested before the ecolabel field.
const GROUPS = [
  {
    group: FIELD_GROUP_SUSTAINABILITY_DETAILS,
    patterns: [
      'supportingdetails',
      'supportinginformation',
      'sustainabilitydetails',
      'sustainabilityevidence',
      'criteriadetails',
      'ecolabeldetails',
    ],
  },
  {
    group: FIELD_GROUP_ECOLABELS,
    patterns: ['ecolabel', 'sustainabilitycriteria'],
  },
  {
    // "Where is this product dispatched from?" (key: dispatched_location). Tested
    // before delivery so a label that also mentions dispatch times can't steal it.
    group: FIELD_GROUP_DISPATCH_LOCATION,
    patterns: ['dispatchedlocation', 'dispatchlocation', 'dispatchedfrom', 'shipsfrom'],
  },
  {
    group: FIELD_GROUP_DELIVERY,
    patterns: ['processinganddelivery', 'processingdelivery', 'deliveryestimate', 'dispatchtime'],
  },
  {
    group: FIELD_GROUP_RETURNS,
    patterns: ['returnspolicy', 'returnpolicy'],
  },
];

/**
 * Work out whether a hosted listing field belongs to one of the groups that the
 * product page renders in its own bespoke section.
 *
 * @param {Object} fieldConfig a single entry of config.listing.listingFields
 * @returns {string|null} one of the FIELD_GROUP_* constants, or null
 */
export const classifyListingField = fieldConfig => {
  const key = normalize(fieldConfig?.key);
  const label = normalize(fieldConfig?.showConfig?.label || fieldConfig?.saveConfig?.label);

  const matched = GROUPS.find(({ patterns }) =>
    patterns.some(pattern => key.includes(pattern) || label.includes(pattern))
  );
  return matched ? matched.group : null;
};

/**
 * Fields that are for the seller's own bookkeeping and are never shown to
 * shoppers, e.g. "SKU or product reference".
 *
 * "SKU" is matched as a whole word (so "sku", "productSku" and "SKU code" all
 * count, but a word that merely contains the letters, like "husky", doesn't).
 *
 * @param {Object} fieldConfig a single entry of config.listing.listingFields
 * @returns {boolean}
 */
export const isAlwaysHiddenListingField = fieldConfig => {
  const key = fieldConfig?.key;
  const label = fieldConfig?.showConfig?.label || fieldConfig?.saveConfig?.label;

  const hasSkuWord = value =>
    String(value == null ? '' : value)
      // Split camelCase keys ("productSku") before lowercasing.
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .includes('sku');
  const hasProductReference = value =>
    ['productreference', 'productref'].some(pattern => normalize(value).includes(pattern));

  return [key, label].some(value => hasSkuWord(value) || hasProductReference(value));
};

/**
 * Split the hosted listing fields into the groups the product page handles
 * itself and "the rest", which keep their default rendering.
 *
 * @param {Array} listingFieldConfigs config.listing.listingFields
 * @returns {Object} { grouped: { [group]: fieldConfig }, isGroupedField: fn }
 */
export const groupListingFields = (listingFieldConfigs = []) => {
  const grouped = listingFieldConfigs.reduce((acc, fieldConfig) => {
    const group = classifyListingField(fieldConfig);
    // First match wins, so a marketplace with two similarly named fields still
    // renders predictably rather than flip-flopping.
    return group && !acc[group] ? { ...acc, [group]: fieldConfig } : acc;
  }, {});

  const groupedKeys = new Set(Object.values(grouped).map(fieldConfig => fieldConfig.key));

  return {
    grouped,
    isGroupedField: fieldConfig => groupedKeys.has(fieldConfig?.key),
  };
};

/**
 * Read the value a seller entered for a hosted field, together with its label,
 * so the product page can render it under its own heading.
 *
 * @param {Object} fieldConfig a single entry of config.listing.listingFields
 * @param {Object} publicData listing.attributes.publicData
 * @param {Object} metadata listing.attributes.metadata
 * @returns {Object|null} { key, label, value } or null when nothing was entered
 */
export const getListingFieldEntry = (fieldConfig, publicData = {}, metadata = {}) => {
  if (!fieldConfig?.key) {
    return null;
  }
  const { key, scope = 'public', showConfig, saveConfig } = fieldConfig;
  const value = scope === 'metadata' ? metadata?.[key] : publicData?.[key];

  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return null;
  }
  return { key, label: showConfig?.label || saveConfig?.label, value };
};

/**
 * Turn the stored value of a hosted field into display text. Sellers usually
 * type these in as free text, but a marketplace admin is free to configure a
 * field as an enum instead — in which case we show the option label rather
 * than the raw slug that Sharetribe stores.
 *
 * @param {*} value the stored value
 * @param {Object} [fieldConfig] a single entry of config.listing.listingFields
 * @returns {string}
 */
export const formatListingFieldValue = (value, fieldConfig) => {
  const labelFor = option =>
    fieldConfig?.enumOptions?.find(o => `${o.option}` === `${option}`)?.label || `${option}`;

  return Array.isArray(value)
    ? value.map(labelFor).join(', ')
    : fieldConfig?.enumOptions
    ? labelFor(value)
    : `${value}`;
};
